import React, { useState } from 'react';
import { Heart, Star, MapPin, Zap, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatNaira } from '../../utils/formatters';
import { getImageUrl } from '../../utils/imageUtils';
import { useAuth } from '../../context/AuthContext';
import { ConditionBadge } from '../../components/common/Badge'; // Import the Badge component

const ProductCard = ({ 
  listing, 
  onFavoriteClick, 
  isMobile = false,
  isFeatured = false,
  isFavorite: initialIsFavorite = false,
  onAddToCart,
  showAddToCart = false,
  showSelection = false,
  isSelected = false,
  isLoading = false,
  onSelect = () => {}
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { user } = useAuth();

  const handleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Call the parent's onFavoriteClick handler if provided
    if (onFavoriteClick) {
      // Pass the OPPOSITE of current state since we want to toggle
      onFavoriteClick(listing.listing_id, !initialIsFavorite);
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(listing);
    }
  };

  const handleSelect = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSelect) {
      onSelect(listing.listing_id);
    }
  };

  const mainImage = listing.images && listing.images.length > 0 
    ? listing.images[0].image_url 
    : null;

  // Mobile-optimized layout
  if (isMobile) {
    return (
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100 group">
        <Link to={`/listing/${listing.listing_id}`} className="block">
          {/* Image Container */}
          <div className="relative aspect-square overflow-hidden">
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 animate-pulse"></div>
            )}
            {mainImage ? (
              <img
                src={getImageUrl(listing.images[0].image_url)}
                alt={listing.title}
                className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="text-4xl opacity-20">📦</div>
              </div>
            )}
            
            {/* Selection Checkbox */}
            {showSelection && (
              <button
                onClick={handleSelect}
                className="absolute top-2 left-2 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-sm z-10"
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  isSelected 
                    ? 'bg-orange-500 border-orange-500' 
                    : 'bg-white border-gray-300'
                }`}>
                  {isSelected && (
                    <span className="text-white text-xs font-bold">✓</span>
                  )}
                </div>
              </button>
            )}
            
            {/* Favorite Button */}
            <button
              onClick={handleFavorite}
              disabled={isLoading}
              className={`absolute top-2 ${showSelection ? 'right-10' : 'right-2'} p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors z-10 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Heart 
                className={`w-4 h-4 ${
                  initialIsFavorite 
                    ? 'fill-red-500 text-red-500' 
                    : user 
                      ? 'text-gray-600 hover:text-red-500' 
                      : 'text-gray-400'
                }`}
              />
            </button>

            {/* Add to Cart Button */}
            {showAddToCart && (
              <button
                onClick={handleAddToCart}
                className="absolute bottom-2 right-2 p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors shadow-sm z-10"
              >
                <ShoppingCart className="w-4 h-4" />
              </button>
            )}
            
            {/* Boosted Badge */}
            {listing.is_boosted && (
              <div className={`absolute ${showSelection ? 'top-12' : 'top-2'} left-2 px-2 py-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-xs font-medium rounded-full flex items-center`}>
                <Zap className="w-3 h-3 mr-1" />
                
              </div>
            )}
            
            {/* Condition Badge - UPDATED */}
            <div className={`absolute ${showAddToCart ? 'bottom-12' : 'bottom-2'} left-2`}>
              <ConditionBadge 
                condition={listing.condition} 
                size="small"
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-3">
            {/* Title */}
            <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1">
              {listing.title}
            </h3>

            {/* Price */}
            <div className="flex items-center justify-between mb-2">
              <div className="text-base font-bold text-gray-900">
                {formatNaira(listing.price)}
              </div>
              {listing.is_negotiable === 1 && (
                <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                  Negotiable
                </span>
              )}
            </div>
            
            {/* Category */}
            <p className="text-xs text-gray-500 mb-2 line-clamp-1">
              {listing.category_name}
            </p>
            
            {/* Location & Rating */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center">
                <MapPin className="w-3 h-3 mr-1" />
                <span className="truncate max-w-[80px]">{listing.state_name || 'Nationwide'}</span>
              </div>
              <div className="flex items-center">
                <Star className="w-3 h-3 text-yellow-400 mr-1 fill-current" />
                <span>{listing.rating || '0.0'}</span>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 group relative">
      {/* Selection Checkbox */}
      {showSelection && (
        <button
          onClick={handleSelect}
          className="absolute top-3 left-3 p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors shadow-sm z-10"
        >
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
            isSelected 
              ? 'bg-orange-500 border-orange-500' 
              : 'bg-white border-gray-300'
          }`}>
            {isSelected && (
              <span className="text-white text-xs font-bold">✓</span>
            )}
          </div>
        </button>
      )}

      <Link to={`/listing/${listing.listing_id}`} className="block">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden">
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 animate-pulse"></div>
          )}
          {mainImage ? (
            <img
              src={getImageUrl(listing.images[0].image_url)}
              alt={listing.title}
              className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
              <div className="text-6xl opacity-20">📦</div>
            </div>
          )}
          
          {/* Favorite Button */}
          <button
            onClick={handleFavorite}
            disabled={isLoading}
            className={`absolute top-3 ${showSelection ? 'right-12' : 'right-3'} p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-sm z-10 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Heart 
              className={`w-5 h-5 ${
                initialIsFavorite 
                  ? 'fill-red-500 text-red-500' 
                  : user 
                    ? 'text-gray-600 hover:text-red500' 
                    : 'text-gray-400'
              }`}
            />
          </button>

          {/* Add to Cart Button */}
          {showAddToCart && (
            <button
              onClick={handleAddToCart}
              className="absolute bottom-3 right-3 p-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors shadow-lg z-10"
              title="Add to cart"
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
          )}
          
          {/* Boosted Badge */}
          {listing.is_boosted && (
            <div className={`absolute ${showSelection ? 'top-14' : 'top-3'} left-3 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-sm font-medium rounded-full flex items-center shadow-sm`}>
              <Zap className="w-4 h-4 mr-1" />
              
            </div>
          )}
          
          {/* Condition Badge - UPDATED */}
          <div className={`absolute ${showAddToCart ? 'bottom-14' : 'bottom-3'} left-3`}>
            <ConditionBadge 
              condition={listing.condition} 
              size="medium"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Title */}
          <h3 className="font-bold text-gray-900 text-base mb-2 line-clamp-2 hover:text-[#ff660e] transition-colors">
            {listing.title}
          </h3>
          
          {/* Category */}
          <p className="text-sm text-gray-500 mb-3 line-clamp-1">
            {listing.category_name}
          </p>
          
          {/* Price */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-xl font-bold text-gray-900">
              {formatNaira(listing.price)}
            </div>
            {listing.is_negotiable === 1 && (
              <span className="text-sm text-purple-600 bg-purple-50 px-3 py-1.5 rounded-lg">
                Price negotiable
              </span>
            )}
          </div>
          
          {/* Location & Rating */}
          <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t border-gray-100">
            <div className="flex items-center">
              <MapPin className="w-4 h-4 mr-2" />
              <span>{listing.state_name || 'Nationwide'}</span>
            </div>
            <div className="flex items-center">
              <Star className="w-4 h-4 text-yellow-400 mr-1 fill-current" />
              <span>{listing.seller_rating || 'No ratings'}</span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;